import React, {useCallback, useEffect} from 'react';
import {Grid} from "@mui/material";
import {PublisherService} from "../../../services/publisher.service";
import {useLocation, useParams} from "react-router-dom";
import {showSnackSuccess, toggleLoader} from "../../../store/global/global-slice";
import {useDispatch} from "react-redux";
import {Tab, Tabs} from "@material-ui/core";
import {CustomTabPanel} from "../../../components/ui-base/tab-panel";
import {PaginationModel} from "../../../models/pagination.model";
import {NewPublisherBookShort} from "../../../components/publisher/books/new-publisher-book-short";
import {EmptyBookShort} from "../../../components/author/books/empty-book-short";
import {StyledButton} from "../../../components/ui-base/button";
import {PublisherSimilarBookModel} from "../../../models/publisher/publisher-similar-book.model";
import styled from "styled-components";
import {DetailsWrapper} from "../../../components/details-wrapper/details-wrapper";
import {DetailsPropWrapper} from "../../../components/ui-base/details-prop-wrapper";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Link} from "react-router-dom";
import {SimilarBookListModel} from "../../../models/publisher/similar-book-list.model";
import {useTranslation} from "react-i18next";
import {Colors} from "../../../theme/colors";
import {SmallBookCover} from "../../../components/shared/small-book-cover";

const SimilarBooksToMyPage = () => {
    const [bookId, setBookId] = React.useState(null);
    const [tabActive, setTabActive] = React.useState(0);
    const [manuscriptsFromProposition, setManuscriptsFromProposition] = React.useState<PaginationModel<SimilarBookListModel[]>>(null);
    const [manuscriptsFromMarket, setManuscriptsFromMarket] = React.useState<PaginationModel<SimilarBookListModel[]>>(null);
    const [exampleBookView, setExampleBookView] = React.useState<boolean>(false);
    const [book, setBook] = React.useState<PublisherSimilarBookModel>(null);
    const {id} = useParams<{ id: string }>();
    const dispatch = useDispatch();
    const location = useLocation();
    const {t} = useTranslation(['publisher', 'common']);

    const loadManuscriptFromProposition = useCallback(async (page: number, perPage: number, id?: string) => {
        const data = await PublisherService.getSimilarBooks(id || bookId, page, perPage);
        setManuscriptsFromProposition(data);
    }, [bookId]);

    const loadManuscriptToExampleFromMarket = useCallback(async (page: number, perPage: number, id?: string) => {
        const data = await PublisherService.getSimilarBooksToExampleFromMarket(id || bookId, page, perPage);
        setManuscriptsFromMarket(data);
    }, [bookId]);

    const loadManuscriptFromMarket = useCallback(async (page: number, perPage: number, id?: string) => {
        const data = await PublisherService.getSimilarBooksFromMarket(id || bookId, page, perPage);
        setManuscriptsFromMarket(data);
    }, [bookId]);

    const loadManuscriptToExampleFromProposition = useCallback(async (page: number, perPage: number, id?: string) => {
        const data = await PublisherService.getSimilarBooksToExample(id || bookId, page, perPage);
        setManuscriptsFromProposition(data);
    }, [bookId]);

    const loadManuscripts = useCallback(async (id: string) => {
        dispatch(toggleLoader(true));

        await loadManuscriptFromMarket(0, 8, id);
        await loadManuscriptFromProposition(0, 8, id);

        dispatch(toggleLoader(false));
    }, [dispatch, loadManuscriptFromMarket, loadManuscriptFromProposition]);

    const loadManuscriptsToExampleBook = useCallback(async (id: string) => {
        dispatch(toggleLoader(true));

        await loadManuscriptToExampleFromMarket(0, 8, id);
        await loadManuscriptToExampleFromProposition(0, 8, id);

        dispatch(toggleLoader(false));
    }, [dispatch, loadManuscriptToExampleFromMarket, loadManuscriptToExampleFromProposition]);

    const loadNextManuscriptFromProposition = async () => {
        if (manuscriptsFromProposition.next) {
            await loadManuscriptFromProposition(manuscriptsFromProposition.results.length / 8, 8);
        }
    }

    const loadNextManuscriptFromMarket = async () => {
        if (manuscriptsFromMarket.next) {
            await loadManuscriptFromMarket(manuscriptsFromMarket.results.length / 8, 8);
        }
    }

    const saveToBookmark = async (application: SimilarBookListModel) => {
        const res = await PublisherService.marketAddToSave(application.id);

        if (res) {
            dispatch(showSnackSuccess(t('similarBooksToMy.manuscriptSaved')));
            if (exampleBookView) {
                await loadManuscriptToExampleFromMarket(0, 8);
            } else {
                await loadManuscriptFromMarket(0, 8);
            }
        }
    }

    const askForPermission = async (application: SimilarBookListModel) => {
        const res = await PublisherService.askForPermission(application.id);

        if (res) {
            dispatch(showSnackSuccess(t('similarBooksToMy.askAuthor')));
            if (exampleBookView) {
                await loadManuscriptToExampleFromMarket(0, 8);
            } else {
                await loadManuscriptFromMarket(0, 8);
            }
        }
    }

    const loadPublisherBook = useCallback(async (id: string) => {
        const res = await PublisherService.getPublisherBook(id);

        setBook(res);
    }, []);

    const loadExampleBook = useCallback(async (id: string) => {
        const res = await PublisherService.getPublisherExampleBook(id);

        setBook(res);
    }, []);

    useEffect(() => {
        if (id) {
            setBookId(id);

            if (location.pathname.search('example') !== -1) {
                setExampleBookView(true);
                loadManuscriptsToExampleBook(id);
                loadExampleBook(id);
            } else {
                loadManuscripts(id);
                loadPublisherBook(id);
            }
        }
    }, [id, location.pathname, loadManuscriptsToExampleBook, loadExampleBook, loadManuscripts, loadPublisherBook]);

    return (
        <DetailsWrapper description={t('similarBooksToMy.similarToYourTitle')}>
            <Grid container direction={"column"}>
                {book &&
                    <Grid item container direction={"row"}>
                        <Grid item style={{marginRight: '1rem'}}>
                            <Link to="/publisher/similar-books/list">
                                <ArrowBackIcon style={{fontSize: '1.5rem'}}/>
                            </Link>
                        </Grid>

                        <Grid item>
                            {(!book.cover || !book.cover.file) &&
                                <SmallBookCover
                                    title={book?.title}
                                    description={book?.description}
                                    author={book.authors}
                                />
                            }
                            {book.cover?.file &&
                                <StyledImageContainer item>
                                    <StyledImg src={book.cover.file}/>
                                </StyledImageContainer>
                            }
                        </Grid>

                        <StyledBookInfoGrid item>
                            <DetailsPropWrapper header={t('common:isbn') + ':'} text={book?.isbn || '-'}
                                                style={{marginBottom: '1rem'}}/>
                            <DetailsPropWrapper header={t('common:authors') + ':'} text={book?.authors || '-'}
                                                style={{marginBottom: '1rem'}}/>

                            {/*<a href={book.content_full.file} target="_blank" rel="noreferrer">*/}
                            {/*    <StyledButton type={"button"} color={"pink"} style={{marginTop: '1rem'}}>Pobierz książkę</StyledButton>*/}
                            {/*</a>*/}
                        </StyledBookInfoGrid>
                    </Grid>
                }

                <Grid item style={{marginTop: '2rem'}}>
                    <Tabs value={tabActive} onChange={(event, newValue) => {
                        setTabActive(newValue)
                    }}>
                        <Tab
                            label={`${t('similarBooksToMy.fromPropositions')} (${manuscriptsFromProposition?.count || 0})`}/>
                        <Tab label={`${t('similarBooksToMy.fromMarket')} (${manuscriptsFromMarket?.count || 0})`}/>
                    </Tabs>

                    <CustomTabPanel value={tabActive} index={0}>
                        <Grid item container direction={"row"} spacing={2}>
                            {manuscriptsFromProposition?.results.map((application: SimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application} similarBook={true}/>
                            ))}
                        </Grid>

                        {manuscriptsFromProposition?.next &&
                            <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                                <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                    loadNextManuscriptFromProposition()
                                }}>
                                    {t('similarBooksToMy.showNext')}
                                </StyledButton>
                            </Grid>
                        }

                        {manuscriptsFromProposition?.results.length === 0 &&
                            <EmptyBookShort
                                title={t('similarBooksToMy.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }
                    </CustomTabPanel>

                    <CustomTabPanel value={tabActive} index={1}>
                        <Grid item container direction={"row"} spacing={2}>
                            {manuscriptsFromMarket?.results.map((application: SimilarBookListModel, index) => (
                                <NewPublisherBookShort key={index} manuscript={application} market={true}
                                                       similarBook={true} saveToBookmark={() => {
                                    saveToBookmark(application)
                                }} askForPermission={() => {
                                    askForPermission(application)
                                }}/>
                            ))}
                        </Grid>

                        {manuscriptsFromMarket?.next &&
                            <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                                <StyledButton type={"button"} color={"transparent"} onClick={() => {
                                    loadNextManuscriptFromMarket()
                                }}>
                                    {t('similarBooksToMy.showNext')}
                                </StyledButton>
                            </Grid>
                        }

                        {manuscriptsFromMarket?.results.length === 0 &&
                            <EmptyBookShort
                                title={t('similarBooksToMy.notFound')}
                                description={''}
                                showButton={false}
                            />
                        }
                    </CustomTabPanel>
                </Grid>
            </Grid>
        </DetailsWrapper>
    );
}

const StyledImageContainer = styled(Grid)`
  position: relative;
`;

const StyledImg = styled('img')`
  width: 175px;
  -webkit-box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
  -moz-box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
  box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
`;

const StyledBookInfoGrid = styled(Grid)`
  padding-left: 3rem;
  padding-top: 2rem;
`;

export default SimilarBooksToMyPage;
